/**
 * Per-class balance schedule for a multi-class booking whose classes are
 * auto-charged on different dates — each class's share is charged 15 days
 * before that class (workers utils/class-balance.ts). Shared by the studio
 * dashboard and the admin booking detail page.
 */
import type { BalanceScheduleRow, BalanceScheduleView } from '../../api/studios';
import { formatDateDisplay } from '../../utils/date';

/** Dollars, keeping cents only when there are any ($600 / $517.50). */
export function formatScheduleAmount(cents: number): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function statusText(row: BalanceScheduleRow, audience: 'studio' | 'admin'): { text: string; tone: string } {
  const chargeOn = row.charge_on ? formatDateDisplay(row.charge_on) : '';
  switch (row.status) {
    case 'paid':
      return {
        text: row.paid_at ? `Paid ${formatDateDisplay(row.paid_at)}` : 'Paid',
        tone: 'text-success-600',
      };
    case 'scheduled':
      return {
        text:
          row.paid_cents > 0
            ? `${formatScheduleAmount(row.paid_cents)} paid, rest charges ${chargeOn}`
            : `Charges ${chargeOn}`,
        tone: 'text-warm-600',
      };
    case 'due':
      return {
        text:
          audience === 'admin'
            ? `Unpaid, was due ${chargeOn}`
            : `Due now (was due ${chargeOn})`,
        tone: 'text-danger-600',
      };
    case 'cancelled':
      return {
        text: audience === 'admin' ? 'Class cancelled, not auto-charged' : 'Class cancelled',
        tone: 'text-warm-400',
      };
  }
}

interface BalanceScheduleProps {
  schedule: BalanceScheduleView;
  audience: 'studio' | 'admin';
}

export function BalanceSchedule({ schedule, audience }: BalanceScheduleProps) {
  return (
    <div data-testid="balance-schedule">
      <p className="text-sm text-warm-500 mb-3">
        {audience === 'admin'
          ? "Auto-charged class by class: each class's share is charged to the saved card 15 days before that class."
          : "Your balance is charged class by class: each class's balance is charged automatically to your saved card 15 days before that class."}
      </p>
      <div className="space-y-2">
        {schedule.rows.map((row) => {
          const status = statusText(row, audience);
          return (
            <div key={row.class_id} className="flex items-start justify-between gap-4 text-sm">
              <div className="min-w-0">
                <span className="text-warm-900 font-medium">{row.show_name ?? 'Masterclass'}</span>
                <span className="text-warm-400 ml-2">{formatDateDisplay(row.class_date)}</span>
              </div>
              <div className="text-right flex-shrink-0">
                <span className={row.status === 'cancelled' ? 'text-warm-400 line-through' : 'text-warm-900 font-medium'}>
                  {formatScheduleAmount(row.amount_cents)}
                </span>
                <p className={`text-xs ${status.tone}`}>{status.text}</p>
              </div>
            </div>
          );
        })}
      </div>
      {schedule.next_charge && (
        <p className={`text-sm mt-3 ${schedule.next_charge.retrying ? 'text-danger-600' : 'text-warm-600'}`}>
          {schedule.next_charge.retrying ? (
            <>
              <span className="font-medium">{formatScheduleAmount(schedule.next_charge.amount_cents)}</span> overdue,
              retried on the saved card every day until the class
            </>
          ) : (
            <>
              Next charge: <span className="font-medium">{formatScheduleAmount(schedule.next_charge.amount_cents)}</span>{' '}
              on {formatDateDisplay(schedule.next_charge.date)}
            </>
          )}
        </p>
      )}
    </div>
  );
}
